import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import {
  AlertCircle,
  BedDouble,
  Building2,
  CalendarRange,
  Code2,
  Eye,
  Layers,
  Package,
  RefreshCw,
  Search,
  Tags,
} from "lucide-react";
import { supabase } from "../../../lib/supabase";
import { pmsInfoService } from "../../../services/PmsInfoService";
import type {
  PmsAvailability,
  PmsHotelConfig,
  PmsInspectableHotel,
  PmsRate,
} from "../../../types/pmsInfo";

type TabId =
  | "overview"
  | "room_types"
  | "rate_plans"
  | "products"
  | "rates"
  | "availabilities";

const TABS: Array<{ id: TabId; label: string; icon: typeof Building2 }> = [
  { id: "overview", label: "Hotel Info", icon: Building2 },
  { id: "room_types", label: "Room Types", icon: BedDouble },
  { id: "rate_plans", label: "Rate Plans", icon: Tags },
  { id: "products", label: "Products & Services", icon: Package },
  { id: "rates", label: "Rates", icon: CalendarRange },
  { id: "availabilities", label: "Availabilities", icon: Layers },
];

const PAGE_SIZE = 50;

/** Mirrors the token-based styling used by CountrySelect so both themes work. */
const selectStyles = {
  control: (base: any, state: { isFocused: boolean }) => ({
    ...base,
    minHeight: "42px",
    backgroundColor: "var(--color-surface-alt)",
    borderColor: state.isFocused
      ? "var(--color-border-focus)"
      : "var(--color-border)",
    boxShadow: state.isFocused ? "0 0 0 2px var(--color-border-focus)" : "none",
    "&:hover": { borderColor: "var(--color-border-focus)" },
  }),
  option: (base: any, state: { isSelected: boolean; isFocused: boolean }) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "var(--color-primary)"
      : state.isFocused
        ? "var(--color-surface-alt)"
        : "transparent",
    color: state.isSelected ? "white" : "var(--color-text)",
    cursor: "pointer",
    "&:active": { backgroundColor: "var(--color-primary)" },
  }),
  input: (base: any) => ({ ...base, color: "var(--color-text)" }),
  singleValue: (base: any) => ({ ...base, color: "var(--color-text)" }),
  placeholder: (base: any) => ({
    ...base,
    color: "var(--color-text-secondary)",
  }),
  menu: (base: any) => ({
    ...base,
    backgroundColor: "var(--color-surface)",
    zIndex: 20,
    boxShadow:
      "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    color: "var(--color-text-secondary)",
    "&:hover": { color: "var(--color-text)" },
  }),
};

function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function defaultFrom(): string {
  return toIsoDate(new Date());
}

function defaultTo(): string {
  const to = new Date();
  to.setDate(to.getDate() + 13);
  return toIsoDate(to);
}

function formatBoolean(value: boolean | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return value ? "Yes" : "No";
}

function formatValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

function formatPrice(value: number | null | undefined, currency?: string): string {
  if (value === null || value === undefined) return "—";
  return currency ? `${value} ${currency}` : String(value);
}

function DefinitionRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-sm font-medium text-[var(--color-text-secondary)]">{label}</h4>
      <p className="mt-1 text-sm text-[var(--color-text)]">{value}</p>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center py-8">
      <p className="text-sm text-[var(--color-text-secondary)]">{message}</p>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-start">
      <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-red-600">{message}</p>
    </div>
  );
}

export function PmsInfo() {
  const [hotels, setHotels] = useState<PmsInspectableHotel[]>([]);
  const [hotelsError, setHotelsError] = useState("");
  const [hotelsLoading, setHotelsLoading] = useState(true);
  const [selectedHotelCode, setSelectedHotelCode] = useState("");

  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [showRawJson, setShowRawJson] = useState(false);

  const [config, setConfig] = useState<PmsHotelConfig | null>(null);
  const [configError, setConfigError] = useState("");
  const [configLoading, setConfigLoading] = useState(false);

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [roomTypeFilter, setRoomTypeFilter] = useState("");

  const [rates, setRates] = useState<PmsRate[] | null>(null);
  const [ratesError, setRatesError] = useState("");
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesPage, setRatesPage] = useState(1);

  const [availabilities, setAvailabilities] = useState<PmsAvailability[] | null>(null);
  const [availabilitiesError, setAvailabilitiesError] = useState("");
  const [availabilitiesLoading, setAvailabilitiesLoading] = useState(false);
  const [availabilitiesPage, setAvailabilitiesPage] = useState(1);

  useEffect(() => {
    const loadHotels = async () => {
      try {
        // Only the hotels this SaaS has an active integration with — the same
        // set the PMS gateway will authorise, so a selection never 403s.
        const { data, error } = await supabase
          .from("integrated_hotels")
          .select("hotel_id, hotel_code, hotel_name");

        if (error) throw error;

        type HotelRow = {
          hotel_id?: string;
          hotel_code?: string;
          hotel_name?: string | null;
        };
        const rows = (data ?? []) as HotelRow[];

        const seen = new Set<string>();
        const inspectable = rows
          .filter((row) => Boolean(row.hotel_code))
          .map((row) => ({
            id: (row.hotel_id ?? row.hotel_code) as string,
            hotelCode: row.hotel_code as string,
            name:
              ((row.hotel_name ?? "") as string).trim() ||
              (row.hotel_code as string),
          }))
          // A SaaS can hold several integrations on the same hotel.
          .filter((hotel) => {
            if (seen.has(hotel.hotelCode)) return false;
            seen.add(hotel.hotelCode);
            return true;
          })
          .sort((a, b) => a.name.localeCompare(b.name));

        setHotels(inspectable);
      } catch (err) {
        console.error("Error loading hotels:", err);
        setHotelsError("Failed to load your integrated hotels");
      } finally {
        setHotelsLoading(false);
      }
    };

    void loadHotels();
  }, []);

  const hotelOptions = useMemo(
    () =>
      hotels.map((hotel) => ({
        value: hotel.hotelCode,
        label: `${hotel.name} (${hotel.hotelCode})`,
      })),
    [hotels]
  );

  const loadConfig = useCallback(async (hotelCode: string) => {
    setConfigLoading(true);
    setConfigError("");
    try {
      const data = await pmsInfoService.getHotelConfig(hotelCode, ["all"]);
      setConfig(data);
    } catch (err) {
      setConfig(null);
      setConfigError(
        err instanceof Error ? err.message : "Failed to load PMS configuration"
      );
    } finally {
      setConfigLoading(false);
    }
  }, []);

  // Reset every hotel-scoped result when the selection changes.
  useEffect(() => {
    setConfig(null);
    setConfigError("");
    setRates(null);
    setRatesError("");
    setRatesPage(1);
    setAvailabilities(null);
    setAvailabilitiesError("");
    setAvailabilitiesPage(1);
    setRoomTypeFilter("");

    if (selectedHotelCode) {
      void loadConfig(selectedHotelCode);
    }
  }, [selectedHotelCode, loadConfig]);

  const roomTypeNames = useMemo(() => {
    const map: Record<string, string> = {};
    (config?.room_types ?? []).forEach((roomType) => {
      if (roomType.id) {
        map[roomType.id] = roomType.name || roomType.id;
      }
    });
    return map;
  }, [config?.room_types]);

  const ratePlanNames = useMemo(() => {
    const map: Record<string, string> = {};
    (config?.rates ?? []).forEach((ratePlan) => {
      if (ratePlan.id) {
        map[ratePlan.id] = ratePlan.name || ratePlan.id;
      }
    });
    return map;
  }, [config?.rates]);

  const currency = config?.hotel_info?.currency;

  const loadRates = useCallback(
    async (page: number) => {
      if (!selectedHotelCode) return;
      setRatesLoading(true);
      setRatesError("");
      try {
        const data = await pmsInfoService.getRates(selectedHotelCode, {
          from,
          to,
          roomTypeIds: roomTypeFilter ? [roomTypeFilter] : undefined,
          page,
          pageSize: PAGE_SIZE,
        });
        setRates(data);
        setRatesPage(page);
      } catch (err) {
        setRates(null);
        setRatesError(err instanceof Error ? err.message : "Failed to load rates");
      } finally {
        setRatesLoading(false);
      }
    },
    [selectedHotelCode, from, to, roomTypeFilter]
  );

  const loadAvailabilities = useCallback(
    async (page: number) => {
      if (!selectedHotelCode) return;
      setAvailabilitiesLoading(true);
      setAvailabilitiesError("");
      try {
        const data = await pmsInfoService.getAvailabilities(selectedHotelCode, {
          from,
          to,
          roomTypeIds: roomTypeFilter ? [roomTypeFilter] : undefined,
          page,
          pageSize: PAGE_SIZE,
        });
        setAvailabilities(data);
        setAvailabilitiesPage(page);
      } catch (err) {
        setAvailabilities(null);
        setAvailabilitiesError(
          err instanceof Error ? err.message : "Failed to load availabilities"
        );
      } finally {
        setAvailabilitiesLoading(false);
      }
    },
    [selectedHotelCode, from, to, roomTypeFilter]
  );

  const handleRefresh = () => {
    if (!selectedHotelCode) return;
    void loadConfig(selectedHotelCode);
    if (rates !== null) void loadRates(ratesPage);
    if (availabilities !== null) void loadAvailabilities(availabilitiesPage);
  };

  const rawJson = useMemo(() => {
    switch (activeTab) {
      case "rates":
        return rates;
      case "availabilities":
        return availabilities;
      default:
        return config;
    }
  }, [activeTab, config, rates, availabilities]);

  const renderDateRangeControls = (onLoad: () => void, loading: boolean) => (
    <div className="flex flex-wrap items-end gap-4 mb-6">
      <div className="w-40">
        <label className="form-label">From</label>
        <input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="form-input"
        />
      </div>
      <div className="w-40">
        <label className="form-label">To</label>
        <input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="form-input"
        />
      </div>
      <div className="w-56">
        <label className="form-label">Room type</label>
        <select
          value={roomTypeFilter}
          onChange={(e) => setRoomTypeFilter(e.target.value)}
          className="form-input"
        >
          <option value="">All room types</option>
          {(config?.room_types ?? []).map((roomType) => (
            <option key={roomType.id} value={roomType.id}>
              {roomType.name || roomType.id}
            </option>
          ))}
        </select>
      </div>
      <button
        type="button"
        onClick={onLoad}
        disabled={loading || !from || !to}
        className="btn-primary disabled:opacity-50"
      >
        {loading ? "Loading..." : "Load"}
      </button>
    </div>
  );

  const renderPagination = (
    page: number,
    rowCount: number,
    onChange: (page: number) => void,
    loading: boolean
  ) => (
    <div className="flex items-center justify-between mt-4">
      <p className="text-sm text-[var(--color-text-secondary)]">
        Page {page} — {rowCount} row{rowCount === 1 ? "" : "s"} (max {PAGE_SIZE} per page)
      </p>
      <div className="flex space-x-2">
        <button
          type="button"
          onClick={() => onChange(page - 1)}
          disabled={page <= 1 || loading}
          className="btn-secondary disabled:opacity-50"
        >
          Previous
        </button>
        <button
          type="button"
          onClick={() => onChange(page + 1)}
          disabled={rowCount < PAGE_SIZE || loading}
          className="btn-secondary disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );

  const renderOverview = () => {
    const info = config?.hotel_info;
    if (!info) {
      return <EmptyState message="No hotel information returned by the PMS." />;
    }

    const address = info.address;
    const addressLine = [
      address?.street,
      address?.postal_code,
      address?.city,
      address?.state,
      address?.country,
    ]
      .filter(Boolean)
      .join(", ");

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <DefinitionRow label="Hotel name" value={formatValue(info.name)} />
        <DefinitionRow label="Hotel code" value={formatValue(info.hotel_code)} />
        <DefinitionRow label="PMS hotel code" value={formatValue(info.pms_hotel_code)} />
        <DefinitionRow label="Currency" value={formatValue(info.currency)} />
        <DefinitionRow label="Language" value={formatValue(info.language)} />
        <DefinitionRow label="Active" value={formatBoolean(info.isActive)} />
        <DefinitionRow label="Email" value={formatValue(info.email)} />
        <DefinitionRow label="Phone" value={formatValue(info.phone)} />
        <div className="md:col-span-2">
          <DefinitionRow label="Address" value={addressLine || "—"} />
        </div>
      </div>
    );
  };

  const renderRoomTypes = () => {
    const roomTypes = config?.room_types ?? [];
    if (!roomTypes.length) {
      return <EmptyState message="No room types returned by the PMS." />;
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-[var(--color-border)]">
          <thead className="bg-[var(--color-surface-alt)]">
            <tr>
              <th className="table-header">ID</th>
              <th className="table-header">Name</th>
              <th className="table-header">Code</th>
              <th className="table-header">Min occupancy</th>
              <th className="table-header">Default occupancy</th>
              <th className="table-header">Max occupancy</th>
            </tr>
          </thead>
          <tbody>
            {roomTypes.map((roomType, index) => (
              <tr key={roomType.id ?? index} className="table-row">
                <td className="table-cell">{formatValue(roomType.id)}</td>
                <td className="table-cell text-[var(--color-text)]">
                  {formatValue(roomType.name)}
                </td>
                <td className="table-cell">{formatValue(roomType.code)}</td>
                <td className="table-cell">{formatValue(roomType.min_occupancy)}</td>
                <td className="table-cell">{formatValue(roomType.default_occupancy)}</td>
                <td className="table-cell">{formatValue(roomType.max_occupancy)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderRatePlans = () => {
    const ratePlans = config?.rates ?? [];
    if (!ratePlans.length) {
      return <EmptyState message="No rate plans returned by the PMS." />;
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-[var(--color-border)]">
          <thead className="bg-[var(--color-surface-alt)]">
            <tr>
              <th className="table-header">ID</th>
              <th className="table-header">Name</th>
              <th className="table-header">Code</th>
              <th className="table-header">Active</th>
              <th className="table-header">Enabled</th>
              <th className="table-header">Public</th>
              <th className="table-header">Base rate</th>
            </tr>
          </thead>
          <tbody>
            {ratePlans.map((ratePlan, index) => (
              <tr key={ratePlan.id ?? index} className="table-row">
                <td className="table-cell">{formatValue(ratePlan.id)}</td>
                <td className="table-cell text-[var(--color-text)]">
                  {formatValue(ratePlan.name)}
                </td>
                <td className="table-cell">{formatValue(ratePlan.code)}</td>
                <td className="table-cell">{formatBoolean(ratePlan.isActive)}</td>
                <td className="table-cell">{formatBoolean(ratePlan.isEnabled)}</td>
                <td className="table-cell">{formatBoolean(ratePlan.isPublic)}</td>
                <td className="table-cell">{formatBoolean(ratePlan.isBaseRate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderProducts = () => {
    const products = config?.products_and_services ?? [];
    if (!products.length) {
      return <EmptyState message="No products or services returned by the PMS." />;
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-[var(--color-border)]">
          <thead className="bg-[var(--color-surface-alt)]">
            <tr>
              <th className="table-header">ID</th>
              <th className="table-header">Name</th>
              <th className="table-header">Type</th>
              <th className="table-header">Category</th>
              <th className="table-header">Price incl.</th>
              <th className="table-header">Price excl.</th>
              <th className="table-header">VAT</th>
              <th className="table-header">Bookable</th>
              <th className="table-header">Active</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product, index) => (
              <tr key={product.id ?? index} className="table-row">
                <td className="table-cell">{formatValue(product.id)}</td>
                <td className="table-cell text-[var(--color-text)]">
                  {formatValue(product.name)}
                </td>
                <td className="table-cell">{formatValue(product.type)}</td>
                <td className="table-cell">{formatValue(product.category_name)}</td>
                <td className="table-cell">
                  {formatPrice(product.unit_price_incl, currency)}
                </td>
                <td className="table-cell">
                  {formatPrice(product.unit_price_excl, currency)}
                </td>
                <td className="table-cell">
                  {product.vat_rate !== undefined ? `${product.vat_rate}%` : "—"}
                </td>
                <td className="table-cell">{formatBoolean(product.bookable)}</td>
                <td className="table-cell">{formatBoolean(product.is_active)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderRates = () => (
    <>
      {renderDateRangeControls(() => void loadRates(1), ratesLoading)}
      {ratesError && <ErrorBanner message={ratesError} />}
      {rates === null && !ratesLoading && !ratesError && (
        <EmptyState message="Pick a date range and click Load to fetch rates from the PMS." />
      )}
      {rates !== null && rates.length === 0 && (
        <EmptyState message="No rates returned by the PMS for this date range." />
      )}
      {rates !== null && rates.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[var(--color-border)]">
              <thead className="bg-[var(--color-surface-alt)]">
                <tr>
                  <th className="table-header">Date</th>
                  <th className="table-header">Room type</th>
                  <th className="table-header">Rate plan</th>
                  <th className="table-header">Price</th>
                  <th className="table-header">Min stay</th>
                  <th className="table-header">Max stay</th>
                  <th className="table-header">CTA</th>
                  <th className="table-header">CTD</th>
                  <th className="table-header">Stop sell</th>
                </tr>
              </thead>
              <tbody>
                {rates.map((rate, index) => (
                  <tr
                    key={`${rate.date}-${rate.room_type_id}-${rate.rate_id}-${index}`}
                    className="table-row"
                  >
                    <td className="table-cell">{rate.date}</td>
                    <td className="table-cell text-[var(--color-text)]">
                      {roomTypeNames[rate.room_type_id] ?? rate.room_type_id}
                    </td>
                    <td className="table-cell">
                      {ratePlanNames[rate.rate_id] ?? rate.rate_id}
                    </td>
                    <td className="table-cell">{formatPrice(rate.price, currency)}</td>
                    <td className="table-cell">{formatValue(rate.min_stay)}</td>
                    <td className="table-cell">{formatValue(rate.max_stay)}</td>
                    <td className="table-cell">{formatBoolean(rate.arrival_closed)}</td>
                    <td className="table-cell">{formatBoolean(rate.departure_closed)}</td>
                    <td className="table-cell">
                      <span
                        className={`status-badge ${
                          rate.stop_sell ? "status-error" : "status-inactive"
                        }`}
                      >
                        {rate.stop_sell ? "Closed" : "Open"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPagination(
            ratesPage,
            rates.length,
            (page) => void loadRates(page),
            ratesLoading
          )}
        </>
      )}
    </>
  );

  const renderAvailabilities = () => (
    <>
      {renderDateRangeControls(
        () => void loadAvailabilities(1),
        availabilitiesLoading
      )}
      {availabilitiesError && <ErrorBanner message={availabilitiesError} />}
      {availabilities === null && !availabilitiesLoading && !availabilitiesError && (
        <EmptyState message="Pick a date range and click Load to fetch availabilities from the PMS." />
      )}
      {availabilities !== null && availabilities.length === 0 && (
        <EmptyState message="No availabilities returned by the PMS for this date range." />
      )}
      {availabilities !== null && availabilities.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[var(--color-border)]">
              <thead className="bg-[var(--color-surface-alt)]">
                <tr>
                  <th className="table-header">Date</th>
                  <th className="table-header">Room type</th>
                  <th className="table-header">Rooms available</th>
                </tr>
              </thead>
              <tbody>
                {availabilities.map((availability, index) => (
                  <tr
                    key={`${availability.date}-${availability.room_type_id}-${index}`}
                    className="table-row"
                  >
                    <td className="table-cell">{availability.date}</td>
                    <td className="table-cell text-[var(--color-text)]">
                      {roomTypeNames[availability.room_type_id] ??
                        availability.room_type_id}
                    </td>
                    <td className="table-cell">
                      <span
                        className={`status-badge ${
                          availability.availability > 0
                            ? "status-active"
                            : "status-error"
                        }`}
                      >
                        {availability.availability}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPagination(
            availabilitiesPage,
            availabilities.length,
            (page) => void loadAvailabilities(page),
            availabilitiesLoading
          )}
        </>
      )}
    </>
  );

  const renderTabContent = () => {
    const isConfigTab = activeTab !== "rates" && activeTab !== "availabilities";

    if (isConfigTab) {
      if (configLoading) {
        return (
          <div className="extranet-container-loading">
            <div className="px-6 py-4 text-center text-sm text-[var(--color-text-secondary)]">
              Loading PMS configuration...
            </div>
          </div>
        );
      }
      if (configError) {
        return <ErrorBanner message={configError} />;
      }
      if (!config) {
        return <EmptyState message="No PMS configuration loaded." />;
      }
    }

    switch (activeTab) {
      case "overview":
        return renderOverview();
      case "room_types":
        return renderRoomTypes();
      case "rate_plans":
        return renderRatePlans();
      case "products":
        return renderProducts();
      case "rates":
        return renderRates();
      case "availabilities":
        return renderAvailabilities();
      default:
        return null;
    }
  };

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center">
            <Eye className="h-6 w-6 text-indigo-600 mr-2" />
            <h2 className="text-2xl font-bold text-[var(--color-text)]">PMS Info</h2>
            <span className="ml-3 status-badge status-inactive">Read only</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setShowRawJson((value) => !value)}
              disabled={!selectedHotelCode}
              className="btn-secondary disabled:opacity-50 inline-flex items-center"
            >
              <Code2 className="h-4 w-4 mr-2" />
              {showRawJson ? "Hide raw JSON" : "Show raw JSON"}
            </button>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={!selectedHotelCode || configLoading}
              className="btn-primary disabled:opacity-50 inline-flex items-center"
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${configLoading ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
          </div>
        </div>
        <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
          Live view of what the connectors return for a hotel: configuration, room
          types, rate plans, products, rates and availabilities. Nothing on this page
          writes back to the PMS. Only hotels you have an active integration
          with are listed.
        </p>
      </div>

      <div className="extranet-container-body">
        {hotelsError && <ErrorBanner message={hotelsError} />}

        <div className="flex flex-wrap items-end gap-4 mb-6">
          <div className="w-96">
            <label className="form-label">Hotel</label>
            <Select
              value={
                hotelOptions.find((option) => option.value === selectedHotelCode) ??
                null
              }
              onChange={(option) => setSelectedHotelCode(option?.value ?? "")}
              options={hotelOptions}
              isClearable
              isSearchable
              isLoading={hotelsLoading}
              placeholder={
                hotelsLoading ? "Loading hotels..." : "Search by name or hotel code..."
              }
              noOptionsMessage={() => "No matching hotel"}
              styles={selectStyles}
              components={{
                DropdownIndicator: () => (
                  <div className="px-2">
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                ),
              }}
            />
          </div>
        </div>

        {!selectedHotelCode ? (
          <EmptyState
            message={
              hotels.length === 0 && !hotelsLoading
                ? "No integrated hotel yet. Connect a hotel first to inspect its PMS data."
                : "Select a hotel to inspect the data its PMS connector returns."
            }
          />
        ) : (
          <>
            <div className="border-b border-[var(--color-border)]">
              <nav className="-mb-px flex flex-wrap gap-x-8">
                {TABS.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        flex items-center py-4 px-1 border-b-2 font-medium text-sm
                        ${
                          activeTab === tab.id
                            ? "border-indigo-500 text-indigo-600"
                            : "border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:border-[var(--color-border)]"
                        }
                      `}
                    >
                      <Icon className="h-5 w-5 mr-2" />
                      {tab.label}
                    </button>
                  );
                })}
              </nav>
            </div>

            <div className="mt-6">{renderTabContent()}</div>

            {showRawJson && (
              <div className="mt-6">
                <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                  Raw response
                </h3>
                <pre className="p-4 rounded-md bg-[var(--color-surface-alt)] text-xs text-[var(--color-text)] overflow-x-auto max-h-96">
                  {JSON.stringify(rawJson, null, 2)}
                </pre>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
